import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Mail, Phone, MapPin, CreditCard } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { isValidCvr } from '@/lib/cvr';
import { useClinic, useUpdateClinicSettings, type ClinicRow } from '@/hooks/useClinicSettings';
import { toast } from 'react-hot-toast';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Select } from '@/components/ui/Select';
import type { Json } from '@/types/database.generated';
import {
  FieldWithIcon,
  PlainField,
  SaveBar,
  SectionCard,
  SettingsFormGrid,
  SettingsFullField,
  SettingsLoading,
} from './settingsChrome';

export { FieldWithIcon, PlainField, SaveBar, SectionCard } from './settingsChrome';

type ClinicType = ClinicRow['type'];

interface AddressShape {
  street: string;
  zip: string;
  city: string;
  country: string;
}

const EMPTY_ADDRESS: AddressShape = { street: '', zip: '', city: '', country: 'Danmark' };

/**
 * Chrome ignores `autocomplete="off"` and still builds a Save address profile
 * from labels such as street / postal code / city / phone / email. `one-time-code`
 * is a real token that is not an address part, so those fields stay editable
 * clinic data and are not offered for saving when the admin leaves Generelt
 * (Indstillinger lands here, then Integrationer removes the form).
 */
const CLINIC_RECORD_AUTOCOMPLETE = 'one-time-code';

/** Ids avoid address tokens such as street, zip, city, email, and phone. */
const CLINIC_RECORD_FIELD = {
  email: 'clinic-record-inbox',
  phone: 'clinic-record-reach',
  street: 'clinic-record-line',
  zip: 'clinic-record-zone',
  city: 'clinic-record-spot',
  country: 'clinic-record-realm',
} as const;

function readAddress(value: Json): AddressShape {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Record<string, Json>;
    return {
      street: typeof v.street === 'string' ? v.street : '',
      zip: typeof v.zip === 'string' ? v.zip : '',
      city: typeof v.city === 'string' ? v.city : '',
      country: typeof v.country === 'string' ? v.country : 'Danmark',
    };
  }
  return EMPTY_ADDRESS;
}

const CLINIC_TYPES: ClinicType[] = ['psychologist', 'dentist', 'specialist', 'emdr_therapist'];

export function ClinicGeneral() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { data: clinic, isLoading, error } = useClinic(activeClinicId);

  if (isLoading) return <SettingsLoading />;
  if (error) return <ErrorAlert message={t('settings.load_error')} />;
  if (!clinic) return <ErrorAlert message={t('settings.no_clinic')} />;

  // Mount the inputs with the clinic record already in place. An empty first
  // paint makes Chrome record "" as the initial value and treat the later
  // clinic address as something the user just typed.
  return <ClinicGeneralForm key={clinic.id} clinic={clinic} />;
}

function ClinicGeneralForm({ clinic }: { clinic: ClinicRow }) {
  const { t } = useTranslation();
  const { activeClinicId, activeClinic } = useActiveClinic();
  const { hasPermission } = useAuth();
  const update = useUpdateClinicSettings(activeClinicId);

  const canEdit = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'settings_general', 'update'),
  );

  const [name, setName] = useState(clinic.name);
  const [clinicType, setClinicType] = useState<ClinicType>(clinic.type);
  const [email, setEmail] = useState(clinic.email ?? '');
  const [phone, setPhone] = useState(clinic.phone ?? '');
  const [cvr, setCvr] = useState(clinic.cvr ?? '');
  const [cvrSaveAttempted, setCvrSaveAttempted] = useState(false);
  const [address, setAddress] = useState<AddressShape>(() => readAddress(clinic.address));
  const [questionnaireDefaultLocale, setQuestionnaireDefaultLocale] = useState<'da-DK' | 'en-DK'>(
    ((clinic as Record<string, unknown>).questionnaire_default_locale as 'da-DK' | 'en-DK') ??
      'da-DK',
  );

  const applyClinic = (row: ClinicRow) => {
    setName(row.name);
    setClinicType(row.type);
    setEmail(row.email ?? '');
    setPhone(row.phone ?? '');
    setCvr(row.cvr ?? '');
    setCvrSaveAttempted(false);
    setAddress(readAddress(row.address));
    setQuestionnaireDefaultLocale(
      ((row as Record<string, unknown>).questionnaire_default_locale as 'da-DK' | 'en-DK') ??
        'da-DK',
    );
  };

  const skipInitialSync = useRef(true);
  useEffect(() => {
    if (skipInitialSync.current) {
      skipInitialSync.current = false;
      return;
    }
    applyClinic(clinic);
  }, [clinic]);

  const cvrInvalid = cvrSaveAttempted && !isValidCvr(cvr);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValidCvr(cvr)) {
      setCvrSaveAttempted(true);
      return;
    }
    try {
      await update.mutateAsync({
        name,
        type: clinicType,
        email,
        phone,
        cvr: cvr.trim(),
        address: address as unknown as Json,
        questionnaire_default_locale: questionnaireDefaultLocale,
      });
      toast.success(t('settings.saved_toast'));
    } catch (err) {
      console.error('update_clinic_settings failed', err);
      toast.error(t('settings.save_error_toast'));
    }
  };

  return (
    <form autoComplete="off" onSubmit={onSubmit} className="flex min-w-0 flex-col gap-s4">
      <SectionCard
        title={t('settings.general.title')}
        subtitle={t('settings.general.subtitle')}
        readOnly={!canEdit}
      >
        <SettingsFormGrid className="mt-0">
          <FieldWithIcon
            id="clinic-name"
            label={t('settings.general.clinic_name')}
            icon={Building2}
            value={name}
            onChange={setName}
            disabled={!canEdit}
            required
          />
          <Select
            id="clinic-type"
            label={t('settings.general.clinic_type')}
            value={clinicType}
            onChange={(e) => setClinicType(e.target.value as ClinicType)}
            disabled={!canEdit}
          >
            {CLINIC_TYPES.map((tp) => (
              <option key={tp} value={tp}>
                {t(`settings.general.clinic_type_${tp}`)}
              </option>
            ))}
          </Select>
          <FieldWithIcon
            id={CLINIC_RECORD_FIELD.email}
            label={t('settings.general.email')}
            icon={Mail}
            type="email"
            value={email}
            onChange={setEmail}
            disabled={!canEdit}
            autoComplete={CLINIC_RECORD_AUTOCOMPLETE}
          />
          <FieldWithIcon
            id={CLINIC_RECORD_FIELD.phone}
            label={t('settings.general.phone')}
            icon={Phone}
            type="tel"
            value={phone}
            onChange={setPhone}
            disabled={!canEdit}
            autoComplete={CLINIC_RECORD_AUTOCOMPLETE}
          />
          <FieldWithIcon
            id="clinic-cvr"
            label={t('settings.general.cvr')}
            icon={CreditCard}
            value={cvr}
            onChange={setCvr}
            disabled={!canEdit}
            hint={t('settings.general.cvr_hint')}
            error={cvrInvalid ? t('settings.general.cvr_invalid') : undefined}
          />
        </SettingsFormGrid>
      </SectionCard>

      <SectionCard
        title={t('settings.general.questionnaires_section')}
        subtitle={t('settings.general.questionnaires_subtitle')}
        readOnly={!canEdit}
      >
        <SettingsFormGrid className="mt-0">
          <Select
            id="questionnaire-locale"
            label={t('settings.general.questionnaire_default_locale')}
            value={questionnaireDefaultLocale}
            onChange={(e) => setQuestionnaireDefaultLocale(e.target.value as 'da-DK' | 'en-DK')}
            disabled={!canEdit}
            hint={t('settings.general.questionnaire_default_locale_hint')}
          >
            <option value="da-DK">{t('settings.general.locale_da')}</option>
            <option value="en-DK">{t('settings.general.locale_en')}</option>
          </Select>
        </SettingsFormGrid>
      </SectionCard>

      <SectionCard title={t('settings.general.address_section')} readOnly={!canEdit}>
        <SettingsFormGrid className="mt-0">
          <SettingsFullField>
            <FieldWithIcon
              id={CLINIC_RECORD_FIELD.street}
              label={t('settings.general.address_street')}
              icon={MapPin}
              value={address.street}
              onChange={(v) => setAddress((a) => ({ ...a, street: v }))}
              disabled={!canEdit}
              autoComplete={CLINIC_RECORD_AUTOCOMPLETE}
            />
          </SettingsFullField>
          <PlainField
            id={CLINIC_RECORD_FIELD.zip}
            label={t('settings.general.address_zip')}
            value={address.zip}
            onChange={(v) => setAddress((a) => ({ ...a, zip: v }))}
            disabled={!canEdit}
            autoComplete={CLINIC_RECORD_AUTOCOMPLETE}
          />
          <PlainField
            id={CLINIC_RECORD_FIELD.city}
            label={t('settings.general.address_city')}
            value={address.city}
            onChange={(v) => setAddress((a) => ({ ...a, city: v }))}
            disabled={!canEdit}
            autoComplete={CLINIC_RECORD_AUTOCOMPLETE}
          />
          <SettingsFullField>
            <PlainField
              id={CLINIC_RECORD_FIELD.country}
              label={t('settings.general.address_country')}
              value={address.country}
              onChange={(v) => setAddress((a) => ({ ...a, country: v }))}
              disabled={!canEdit}
              autoComplete={CLINIC_RECORD_AUTOCOMPLETE}
            />
          </SettingsFullField>
        </SettingsFormGrid>
      </SectionCard>

      <SaveBar
        canEdit={canEdit}
        pending={update.isPending}
        onCancel={() => applyClinic(clinic)}
        hint={
          activeClinic && !activeClinic.isSubClinic ? t('settings.general.apply_hint') : undefined
        }
      />
    </form>
  );
}
